import {TestsContentWrapper, TestsPageWrapper} from '@pages/TestsPage.styles';
import PageTitleBlock from '@organisms/PageTitleBlock';
import Button from '@atoms/Button';
import {AddAlt, Group} from '@carbon/icons-react';
import PageSubtitleBlock from '@organisms/PageSubtitleBlock';
import PageSubtitleTextBlock from '@molecules/PageSubtitleTextBlock';
import {theme} from '@styles/theme';
import {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import EmptyBlock from '@molecules/EmptyBlock';
import {getUsers, sendReminder, users} from '@store/slices/users';
import {IUser} from '@types';
import UsersTable from '@organisms/UsersTable';
import AddUserModal from '@organisms/modals/AddUserModal';
import CircleButton from '@atoms/CircleButton';
import {AppDispatch, RootState} from '@store';
import {IDLE, PENDING} from '@constants/loadingState';
import DeleteUserModal from '@organisms/modals/DeleteUserModal';
import {toast} from 'react-toastify';
import Toast from '@atoms/Toast';

export const UsersPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {loading, inviteUserLoading, deleteUsersLoading, sendReminderLoading} = useSelector(
    (state: RootState) => state.users,
  );
  const usersArr: IUser[] = useSelector(users);
  const [selectedUsersIds, setSelectedUsersIds] = useState<string[]>([]);
  const [usersOnDelete, setUsersOnDelete] = useState<number[]>([]);
  const [userOnEdit, setUserOnEdit] = useState<IUser | undefined>();
  const [addUserShow, setAddUserShow] = useState(false);
  const [deleteUserShow, setDeleteUserShow] = useState(false);
  const [wasReminderSent, setWasReminderSent] = useState(false);
  const [reminderUser, setReminderUser] = useState<IUser | undefined>();

  useEffect(() => {
    dispatch(getUsers({}));
  }, []);

  useEffect(() => {
    if (inviteUserLoading === IDLE && deleteUsersLoading === IDLE) {
      dispatch(getUsers({}));
    }
  }, [inviteUserLoading, deleteUsersLoading]);

  useEffect(() => {
    if (wasReminderSent && sendReminderLoading === IDLE) {
      toast.success(
        <Toast
          title="Готово"
          text={`Напоминание выслано пользователю ${reminderUser?.username} на email ${reminderUser?.email}`}
          type="success"
        />,
      );
      setWasReminderSent(false);
      setReminderUser(undefined);
    }
  }, [sendReminderLoading]);

  const onSendReminder = (user_id: number) => {
    const targetUser = usersArr.find(user => {
      return user.id === user_id;
    });
    setReminderUser(targetUser);
    setWasReminderSent(true);
    dispatch(
      sendReminder({
        emails: [targetUser?.email],
      }),
    );
  };

  return (
    <TestsPageWrapper>
      <PageTitleBlock
        title="Пользователи"
        buttons={[
          <Button
            key={'add-user-title'}
            title="Добавить пользователя"
            iconElement={<AddAlt />}
            variant="filled"
            onClick={() => setAddUserShow(true)}
          />,
          <CircleButton
            key={'delete-user-title'}
            menu={[
              {
                title: `Удалить (${selectedUsersIds.length})`,
                onClick: () => {
                  if (selectedUsersIds.length) {
                    setUsersOnDelete(
                      selectedUsersIds.map(id => {
                        return Number(id);
                      }),
                    );
                    setDeleteUserShow(true);
                  }
                },
                color: selectedUsersIds.length
                  ? theme.colors.signalRed00
                  : theme.colors.basic04,
              },
            ]}
          />,
        ]}
      />
      <PageSubtitleBlock
        elementsToLeft={[
          <PageSubtitleTextBlock
            title="Всего пользователей:"
            text={`${usersArr.length}`}
            key="all-users-count"
          />,
          <PageSubtitleTextBlock
            title="Последнее изменение"
            text={`${usersArr[0]?.lastActivity || '-'}`}
            key="users-last-activity"
          />,
        ]}
        elementsToRight={[]}
      />
      <TestsContentWrapper>
        {((!!usersArr.length && loading === IDLE) || loading === PENDING) && (
          <UsersTable
            users={usersArr}
            loading={loading}
            onSort={() => {}}
            onDelete={user_id => {
              setUsersOnDelete([user_id]);
              setDeleteUserShow(true);
            }}
            onEdit={user_id => {
              setUserOnEdit(
                usersArr.find(user => {
                  return user.id === user_id;
                }),
              );
              setAddUserShow(true);
            }}
            onSendReminder={onSendReminder}
            onSelectRow={setSelectedUsersIds}
          />
        )}
        {!usersArr.length && loading === IDLE && (
          <EmptyBlock
            icon={<Group size={32} color={theme.colors.accent00} />}
            text="Здесь пусто. Добавьте пользователей и редакторов для администрирования состояния тестового покрытия."
          />
        )}
      </TestsContentWrapper>
      <AddUserModal
        isOpened={addUserShow}
        user={userOnEdit}
        onCloseRequest={() => {
          setAddUserShow(false);
          setUserOnEdit(undefined);
        }}
      />
      <DeleteUserModal
        isOpened={deleteUserShow}
        onCloseRequest={() => setDeleteUserShow(false)}
        usersIds={usersOnDelete}
      />
    </TestsPageWrapper>
  );
};

export default UsersPage;
